/**
 * Translation keys and community terminology overrides.
 *
 * Deliberately not an i18n framework. English is the only language at launch and the
 * architectural requirement is that a second language be *possible*, not present — so this is a
 * flat key map with interpolation and a per-community override layer, which is enough until a
 * French translation is real and is roughly a hundredth of the weight (see decisions.md,
 * Assumptions).
 *
 * The part that is not deferrable is terminology. ux.md §2 requires that the software say what
 * the community says, and retrofitting that means finding every string in the codebase. So the
 * override layer exists from the start even though the second locale does not.
 */

export * from "./terms.js";
export { enCA } from "./messages/en-CA.js";
export { frCA } from "./messages/fr-CA.js";

import { enCA } from "./messages/en-CA.js";
import { frCA } from "./messages/fr-CA.js";
import { DEFAULT_TERMS, FR_TERMS, type Term, type TermKey, type TermOverrides } from "./terms.js";

export const LOCALES = ["en-CA", "fr-CA"] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en-CA";

/** What a language picker shows. Each language is named in itself, never in English. */
export const LOCALE_NAMES: Record<Locale, string> = {
  "en-CA": "English",
  "fr-CA": "Français",
};

export const MESSAGES: Record<Locale, Messages> = {
  "en-CA": enCA,
  "fr-CA": frCA,
};

/**
 * The base vocabulary for a locale, before the community's own words are layered on.
 *
 * A community that has never touched terminology should still read French throughout when it
 * chooses French, and a community that renamed two words should keep those two renamed in both
 * languages. That is why this is a base rather than a fallback: `resolveTerm` reads the locale's
 * defaults and `terminology_json` overrides them, in that order.
 */
export const LOCALE_TERMS: Record<Locale, Record<TermKey, Term>> = {
  "en-CA": DEFAULT_TERMS,
  "fr-CA": FR_TERMS,
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/**
 * Choose a locale from what the reader wants and what the community actually offers.
 *
 * Order of preference: the person's own saved choice, then their browser's `Accept-Language`,
 * then the community's default. A community that has not enabled French does not get served
 * French because a visitor's phone asked for it — offering a half-translated site is worse than
 * offering an honest English one, and which languages are ready is the community's call (§32).
 *
 * Language tags are matched on the primary subtag, so `fr`, `fr-FR` and `fr-CA` all reach the
 * Canadian French we have rather than falling through to English on a regional mismatch.
 */
export function negotiateLocale(input: {
  preferred?: string | null | undefined;
  acceptLanguage?: string | null | undefined;
  supported?: readonly string[] | null | undefined;
  fallback?: string | null | undefined;
}): Locale {
  const fallback = isLocale(input.fallback) ? input.fallback : DEFAULT_LOCALE;

  const available = (input.supported ?? []).filter(isLocale);
  const offered = available.length > 0 ? available : [fallback];
  const allowed = new Set<Locale>([...offered, fallback]);

  if (isLocale(input.preferred) && allowed.has(input.preferred)) return input.preferred;

  for (const tag of parseAcceptLanguage(input.acceptLanguage)) {
    const match = [...allowed].find((locale) => primary(locale) === primary(tag));
    if (match) return match;
  }

  return fallback;
}

function primary(tag: string): string {
  return (tag.split("-")[0] ?? tag).toLowerCase();
}

/** Tags in descending q-order. Malformed entries are skipped rather than throwing. */
function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(",")
    .map((part) => {
      const [tag = "", ...params] = part.trim().split(";");
      const q = params
        .map((param) => param.trim())
        .find((param) => param.startsWith("q="))
        ?.slice(2);
      const quality = q === undefined ? 1 : Number.parseFloat(q);
      return { tag: tag.trim(), quality: Number.isFinite(quality) ? quality : 0 };
    })
    .filter((entry) => entry.tag !== "" && entry.tag !== "*" && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality)
    .map((entry) => entry.tag);
}

/**
 * The locales stored in `communities.supported_locales_json`, filtered to the ones this build can
 * actually render. A community may not advertise a language the software cannot produce.
 */
export function parseSupportedLocales(value: unknown): Locale[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<Locale>();
  for (const entry of value) if (isLocale(entry)) seen.add(entry);
  return [...seen];
}

/** Interpolation values. Numbers are formatted by the caller — this layer does no formatting. */
export type Vars = Record<string, string | number>;

export type Messages = Record<string, string>;

export interface TranslatorOptions {
  locale?: Locale;
  messages?: Messages;
  terms?: TermOverrides | null;
  /**
   * What to do with a key that has no message. Development throws, so a missing string is found
   * by a test rather than by a volunteer in an arena; production returns the key, because a
   * visible key is a bad screen and a thrown error is no screen at all.
   */
  onMissing?: "throw" | "key";
}

export interface Translator {
  locale: Locale;
  /** Look up an interface string. `{name}` placeholders are replaced from `vars`. */
  t(key: string, vars?: Vars): string;
  /** Look up a domain word, respecting the community's override. */
  term(key: TermKey, count?: number): string;
  /** The same word capitalised for the start of a sentence, without touching later characters —
   *  "Grand Entry" must not become "Grand entry". */
  Term(key: TermKey, count?: number): string;
}

const PLACEHOLDER = /\{(\w+)\}/g;

export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  const filled = template.replace(PLACEHOLDER, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
  // A sentence ends with one full stop, whatever the substitution ended with. English times from
  // `Intl` carry their own — "Registration closes Sunday, August 16 at 12:30 p.m." plus the
  // template's period read "p.m..". Repairing it here rather than dropping the period from the
  // template keeps the sentence a sentence in French, where the time ends in a digit.
  return filled.replace(/\.\.$/, ".");
}

/**
 * Resolve one term through the override layer.
 *
 * An override may supply only `one` or only `other`; the missing form falls back to the default
 * rather than to the supplied form, because "Dancers" is a better plural for an unspecified
 * override than "Dancer" would be.
 */
export function resolveTerm(
  key: TermKey,
  overrides: TermOverrides | null | undefined,
  locale: Locale = DEFAULT_LOCALE,
): Term {
  const fallback = (LOCALE_TERMS[locale] ?? DEFAULT_TERMS)[key];
  const override = overrides?.[key];
  if (!override) return fallback;
  if (typeof override === "string") {
    const word = nonEmpty(override);
    return word ? { one: word, other: word } : fallback;
  }
  return {
    one: nonEmpty(override.one) ?? fallback.one,
    other: nonEmpty(override.other) ?? fallback.other,
  };
}

function nonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function createTranslator(options: TranslatorOptions = {}): Translator {
  const {
    locale = DEFAULT_LOCALE,
    // Defaulting to the locale's own messages rather than to `{}` means choosing French is one
    // argument, not two — a caller that passes a locale and forgets the message table gets French,
    // not a screen full of raw keys.
    messages = MESSAGES[locale] ?? {},
    terms = null,
    onMissing = "key",
  } = options;

  return {
    locale,

    t(key, vars) {
      const template = messages[key];
      if (template === undefined) {
        if (onMissing === "throw") {
          throw new Error(`Missing translation for "${key}" in ${locale}`);
        }
        return key;
      }
      return interpolate(template, vars);
    },

    term(key, count = 1) {
      const resolved = resolveTerm(key, terms, locale);
      return count === 1 ? resolved.one : resolved.other;
    },

    Term(key, count = 1) {
      const word = this.term(key, count);
      return word.charAt(0).toUpperCase() + word.slice(1);
    },
  };
}

/**
 * Terminology arrives from the database as untyped JSON, so it is filtered before use: unknown
 * keys are dropped and non-string values ignored. A community cannot break a screen by putting
 * an object where a word belongs.
 */
export function parseTermOverrides(value: unknown): TermOverrides {
  if (typeof value !== "object" || value === null) return {};
  const result: TermOverrides = {};

  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!(key in DEFAULT_TERMS)) continue;
    const termKey = key as TermKey;

    if (typeof raw === "string") {
      // The common shorthand: a single word, used for both forms until someone says otherwise.
      result[termKey] = { one: raw, other: raw };
      continue;
    }
    if (typeof raw !== "object" || raw === null) continue;

    const candidate = raw as Record<string, unknown>;
    const one = typeof candidate.one === "string" ? candidate.one : undefined;
    const other = typeof candidate.other === "string" ? candidate.other : undefined;
    if (one === undefined && other === undefined) continue;
    result[termKey] = {
      ...(one !== undefined ? { one } : {}),
      ...(other !== undefined ? { other } : {}),
    };
  }

  return result;
}
